/* 
Scaffolded code: this code was scaffolded by the code generator, feel free to change it 
Template: ViewModel.cst
 */
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ViewModelService } from 'src/app/shared/view-model.service';
import { AppState } from 'src/store/appstate';
import { selectAllTimestampTypes } from 'src/store/entityStores/entity-store-selectors';
import { selectTimestampsByCardId } from 'src/store/entityStores/timestamp-selectors';
import { TimestampRecord } from 'src/store/generated/timestamp-record.generated';
import { CardViewModel } from './card-view-model';
import { TimelineItemViewModel } from './timeline-item-view-model';
import { TimestampTypeViewModel } from './timestamp-type-view-model';
import { TimestampViewModel } from './timestamp-view-model';

export class CardTimelineItemViewModel {
  public timelineItem$: Observable<TimelineItemViewModel | undefined>;
  public timestamp$: Observable<TimestampViewModel | undefined>;

  get timestampType$(): Observable<TimestampTypeViewModel | undefined> {
    return combineLatest([this.timestamp$, this.timelineItem$]).pipe(
      map(([ts, tli]) => {
        if (ts != null) {
          return ts.type;
        }

        if (tli != null) {
          return tli.timestampType;
        }

        return undefined;
      }),
    );
  }
  get required$(): Observable<boolean> {
    return this.timelineItem$.pipe(
      map((tl) => {
        if (tl == null) {
          return false;
        }

        return tl.required;
      }),
    );
  }

  get requiredAndCompleted$(): Observable<boolean> {
    return combineLatest([this.required$, this.timestamp$]).pipe(
      map(([req, ts]) => {
        return req === true && ts != null;
      }),
    );
  }

  private get _requiredAndIncomplete$() {
    return combineLatest(this.required$, this.timestamp$).pipe(
      map(([req, ts]) => {
        return req === true && ts == null;
      }),
    );
  }

  private get _anyNextTimestamp$(): Observable<TimestampRecord | undefined> {
    const timestamps$ = this._store.select(selectTimestampsByCardId(this.card.id));
    const timestampTypes$ = this._store.select(selectAllTimestampTypes);
    return combineLatest(this.timelineItem$, timestamps$, timestampTypes$).pipe(
      map(([currentTimelineItem, timestamps, timestampTypes]) => {
        if (currentTimelineItem == null || currentTimelineItem.timestampType == null) {
          return undefined;
        }

        // Get a list of timestamp types that have order greater than the timelineItem
        const nextTimestampTypes = timestampTypes
          .filter((tst) => tst.order > currentTimelineItem.timestampType.order)
          .map((tst) => tst.id)
          .toList();

        // Get the next timestamp
        const nextTimestamp = timestamps.find((ts) => nextTimestampTypes.includes(ts.typeId));
        return nextTimestamp;
      }),
    );
  }

  get requiredIncompleteAndSkipped$(): Observable<boolean> {
    return combineLatest(this._anyNextTimestamp$, this._requiredAndIncomplete$).pipe(
      map(([anyNextTimestamp, incomplete]) => {
        if (incomplete === false) {
          return false;
        }

        // If there is a next timestamp, return true
        return anyNextTimestamp != null;
      }),
    );
  }

  get requiredIncompleteAndNoNext$(): Observable<boolean> {
    return combineLatest([this._anyNextTimestamp$, this._requiredAndIncomplete$]).pipe(
      map(([anyNextTimestamp, incomplete]) => {
        if (incomplete === false) {
          return false;
        }

        return anyNextTimestamp == null;
      }),
    );
  }

  get statusDisplay$(): Observable<string> {
    return combineLatest([
      this.required$,
      this.requiredAndCompleted$,
      this.requiredIncompleteAndSkipped$,
      this.requiredIncompleteAndNoNext$,
    ]).pipe(
      map(
        ([
          required,
          requiredAndCompleted,
          requiredIncompleteAndSkipped,
          requiredIncompleteAndNoNext,
        ]) => {
          if (required !== true) {
            return 'Not required';
          }
          if (requiredAndCompleted === true) {
            return 'Required and completed';
          }

          if (requiredIncompleteAndSkipped === true) {
            return 'Required and skipped';
          }

          if (requiredIncompleteAndNoNext === true) {
            return 'Required and no next timestamp';
          }

          return '';
        },
      ),
    );
  }

  constructor(
    public card: CardViewModel,
    public timelineItemId: string,
    public timestampId: string | undefined,
    private _model: ViewModelService,
    private _store: Store<AppState>,
  ) {
    this.timestamp$ =
      this.timestampId == null ? of(undefined) : this.card.getTimestampById(this.timestampId);

    this.timelineItem$ = this._model.getTimelineItemViewModel(this.timelineItemId);
  }
}
